import { useEffect, useRef, type CSSProperties } from "react";
import { coachSessions } from "./coach";
import "./hour.css";

// A 38-minute session drawn as a dense strip of frames. Scrolling through the scene dims the hour and lifts out the
// few moments a review keeps, while the clock counts down from the whole session to the seconds worth rewatching.
const session = coachSessions.cs2;
const BARS = 96;
const keep = session.moments;
const keepAt = keep.map(m => Math.round((m.at / session.seconds) * (BARS - 1)));
const bars = Array.from({ length: BARS }, (_, i) => { const moment = keepAt.indexOf(i); return { h: moment >= 0 ? 100 : 22 + ((i * 47) % 56) + ((i * 13) % 11), moment }; });
const watchSeconds = keep.reduce((sum, m) => sum + parseInt(m.clip, 10), 0);
const clock = (t: number) => `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(Math.round(t % 60)).padStart(2, "0")}`;

export function HourScene() {
  const root = useRef<HTMLDivElement>(null);
  const time = useRef<HTMLElement>(null);

  useEffect(() => {
    const scene = root.current;
    if (!scene) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const narrow = window.matchMedia("(max-width: 760px)");
    let frame = 0;
    let shown = -1;
    const paint = (p: number) => {
      scene.style.setProperty("--p", p.toFixed(4));
      const seconds = Math.round(session.seconds - (session.seconds - watchSeconds) * Math.min(1, p / .82));
      if (seconds !== shown && time.current) { shown = seconds; time.current.textContent = clock(seconds); }
    };
    const update = () => {
      frame = 0;
      if (reduced.matches) return paint(1);
      const box = scene.getBoundingClientRect();
      // Desktop: the stage is sticky inside a tall track, so progress follows the track. Phones: the scene plays as it crosses the middle of the screen.
      const p = narrow.matches
        ? (window.innerHeight * .85 - box.top) / (window.innerHeight * .7)
        : -box.top / Math.max(1, box.height - window.innerHeight);
      paint(Math.min(1, Math.max(0, p)));
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    const drawn = new IntersectionObserver(([entry]) => { if (entry?.isIntersecting) { scene.classList.add("drawn"); drawn.disconnect(); } }, { rootMargin: "0px 0px -30% 0px" });
    if (reduced.matches) scene.classList.add("drawn"); else drawn.observe(scene);
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => { drawn.disconnect(); window.removeEventListener("scroll", schedule); window.removeEventListener("resize", schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, []);

  return <section className="hour" ref={root} aria-labelledby="hour-heading">
    <div className="hour-stage">
      <div className="hs-wrap hour-inner">
        <h2 id="hour-heading" className="hs-h2 hour-title">Nobody rewatches an hour of footage. <span>Hindset finds the moments that matter.</span></h2>
        <div className="hour-clock" aria-hidden="true"><b ref={time}>{clock(session.seconds)}</b><em><span className="a">of footage</span><span className="b">worth watching</span></em></div>
        <div className="hour-strip" aria-hidden="true">
          {bars.map((bar, i) => <i key={i} className={bar.moment >= 0 ? "keep" : ""} style={{ "--h": `${bar.h}%`, "--i": i, "--k": bar.moment } as CSSProperties} />)}
          {keep.map((m, k) => <span key={m.n} className="hour-pin" style={{ "--x": `${(keepAt[k]! / (BARS - 1)) * 100}%`, "--k": k } as CSSProperties}><b>{m.n}</b></span>)}
        </div>
        <ol className="hour-moments">
          {keep.map((m, k) => <li key={m.n} style={{ "--k": k } as CSSProperties}><time>{m.time}</time><strong>{m.title}</strong><small>{m.clip}</small></li>)}
        </ol>
      </div>
    </div>
  </section>;
}
